import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import ProgressiveImage from '../ProgressiveImage';
import { DiscoverSettings } from '../../services/DiscoverService';
import { FramingSettings } from '../../types';

const getFramingStyle = (framing?: FramingSettings, isMobile = false): React.CSSProperties => {
    if (!framing) return {};
    const current = isMobile ? framing.mobile : framing.desktop;
    if (!current) return {};
    return {
        objectPosition: `${current.x}% ${current.y}%`,
        transform: `scale(${current.scale})`
    };
};

interface UnboxingSectionProps {
    settings: DiscoverSettings;
    onNavigate?: (brand: string) => void;
}

const UnboxingSection: React.FC<UnboxingSectionProps> = ({ settings, onNavigate }) => {
    return (
        <>
            {/* Section 3: Unboxing */}
            <section className="pt-10 md:pt-16 pb-24 md:pb-32 relative overflow-hidden bg-black">
                <div className="max-w-[1800px] mx-auto px-4 md:px-6">
                    <div className="bg-zinc-900 rounded-3xl md:rounded-[3rem] overflow-hidden flex flex-col lg:flex-row items-center">
                        <div className="flex-1 p-8 md:p-12 lg:p-24 space-y-6 md:space-y-8 order-2 lg:order-1">
                            <p className="text-zinc-500 font-mono text-[10px] md:text-xs tracking-widest uppercase italic border-b border-zinc-800 pb-2 inline-block">
                                {settings.unboxing_badge}
                            </p>
                            <h2 className="text-4xl md:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.9] text-white">
                                {/* Split title logic for visual impact */}
                                {settings.unboxing_title.includes('accesorio,') ? (
                                    <>Más que un <span className="text-zinc-700">accesorio,</span> <br className="hidden md:block" /> una pieza de colección.</>
                                ) : (
                                    settings.unboxing_title
                                )}
                            </h2>
                            <p className="text-zinc-400 text-xs md:text-sm lg:text-base leading-relaxed max-w-xl">
                                {settings.unboxing_description}
                            </p>
                            <ul className="space-y-3 md:space-y-4 pt-4">
                                {[settings.unboxing_feature_1, settings.unboxing_feature_2, settings.unboxing_feature_3].map((feature, idx) => (
                                    feature && (
                                        <motion.li
                                            key={idx}
                                            initial={{ opacity: 0, x: -10 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ delay: idx * 0.1 }}
                                            className={`flex items-center gap-3 text-[10px] md:text-xs font-bold uppercase tracking-widest ${idx === 2 ? 'text-zinc-500' : 'text-zinc-300'}`}
                                        >
                                            <div className={`w-1.5 h-1.5 md:w-2 md:h-2 rotate-45 ${idx === 2 ? 'bg-zinc-800' : 'bg-white'}`} />
                                            {feature}
                                        </motion.li>
                                    )
                                ))}
                            </ul>
                        </div>
                        <div className="flex-1 w-full h-[400px] md:h-[600px] bg-zinc-800 relative order-1 lg:order-2 overflow-hidden">
                            {/* Desktop Image */}
                            <div className="hidden md:block absolute inset-0">
                                <ProgressiveImage
                                    src={settings.unboxing_image_url}
                                    alt="Unboxing Experience"
                                    containerClassName="w-full h-full grayscale hover:grayscale-0 transition-[filter] duration-[1.5s] ease-out cursor-pointer"
                                    style={getFramingStyle(settings.unboxing_framing_settings, false)}
                                />
                            </div>
                            {/* Mobile Image */}
                            <div className="block md:hidden absolute inset-0">
                                <ProgressiveImage
                                    src={settings.unboxing_image_mobile_url || settings.unboxing_image_url}
                                    alt="Unboxing Experience"
                                    containerClassName="w-full h-full grayscale hover:grayscale-0 transition-[filter] duration-[1.5s] ease-out cursor-pointer"
                                    style={getFramingStyle(settings.unboxing_framing_settings, true)}
                                />
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-zinc-900 via-transparent to-transparent pointer-events-none" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="pt-10 md:pt-16 pb-32 md:pb-40 text-center space-y-8 md:space-y-12 bg-black px-6">
                <h2 className="text-4xl md:text-6xl lg:text-8xl font-black uppercase tracking-tighter text-zinc-500">
                    {settings.cta_title.split(' ').slice(0, 3).join(' ')} <br />
                    <span className="text-white drop-shadow-[0_0_20px_rgba(255,255,255,0.3)]">
                        {settings.cta_title.split(' ').slice(3).join(' ')}
                    </span>
                </h2>
                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="group bg-white text-black px-8 py-4 md:px-12 md:py-6 rounded-none font-black uppercase text-xs md:text-sm tracking-[0.2em] md:tracking-[0.3em] hover:bg-zinc-200 transition-all flex items-center gap-3 md:gap-4 mx-auto"
                >
                    Volver arriba
                    <ArrowRight className="w-4 h-4 md:w-5 md:h-5 -rotate-90 transition-transform" />
                </motion.button>
            </section>
        </>
    );
};

export default UnboxingSection;
